/**
 * Screens department items — second band of the home timeline (zone
 * 'screens', progress band 0.25–0.5). Visual layout: a wall of four TV frames
 * (each tagged maskRegion 'screens' — their union clips the screens WebGL
 * visual), colour-blob washes and faint systems-grid decorations behind the
 * wall, the cat sitting in front of it (pass 2), and a scatter of isometric
 * building blocks (pass 2) that carry the transition toward spaces.
 *
 * Classification rule for this file: `zone: 'screens'` tag. NOTE the
 * 'systems-block' / 'systems-grid-screens' ids are texture names, not
 * departments — those items are zoned to screens. (A ghost item, treeSprite,
 * was deleted 2026-07-17: its texture never existed in any atlas, so it had
 * never rendered — see the ghost-items commit.)
 *
 * Grouping is for HUMANS ONLY — creation order is owned by index.ts (see the
 * order contract there). Field semantics: see HomeItemConfig / the underlying
 * TimelineItemConfig.
 */
import type { HomeItemConfig } from './homeItems.types';

/**
 * Cluster of empty screen frames (screen-frames.png) behind the TV wall.
 * GHOST WITH A JOB: the texture never existed in any atlas (never renders),
 * but the sprite is a live POSITION ANCHOR — HomeTimeline._initScreensElements
 * assigns it to globals.framesprite, which ScreensDepartment glues the
 * screens mask-image sprite to per tick. Do NOT delete without proving that
 * chain dead end-to-end (kept when the other ghost items were deleted).
 */
export const screenFrames: HomeItemConfig = {
	disable: false,
	id: 'screen-frames',
	texture: 'screen-frames.png',
	alpha: 1e-12,
	relativeX: 0.25,
	relativeY: 0.505,
	easePower: 20,
	parallaxOffsetPower: 0.0,
	parallaxPowerMouse: 0,
	autoScale: false,
	zone: 'screens'
};

/**
 * Soft colour-blob wash (home-bg-colour-blob.png) behind the right side of the
 * screens zone. `texturex` records the texture it used before being re-pointed
 * (systems-grid.png) — renamed-off and inert.
 */
export const screensBgBlob1: HomeItemConfig = {
	disable: false,
	id: 'screens-bg-blob',
	texturex: 'systems-grid.png',
	texture: 'home-bg-colour-blob.png',
	relativeX: 0.9,
	relativeY: 0.45,
	easePower: 3,
	alpha: 0.05,
	parallaxOffsetPower: 0.03,
	parallaxOffsetBase: 0,
	parallaxPowerMouse: 0,
	autoAlpha: false,
	autoRange: 0.9,
	zone: 'screens',
	mobileScaleIncrease: 0.5
};

/**
 * Soft colour-blob wash behind the left edge of the screens zone (relativeX -0.03,
 * just outside the content box).
 */
export const screensBgBlob2: HomeItemConfig = {
	disable: false,
	id: 'screens-bg-blob',
	texturex: 'systems-grid.png',
	texture: 'home-bg-colour-blob.png',
	relativeX: -0.03,
	relativeY: 0.2,
	easePower: 3,
	alpha: 0.05,
	parallaxOffsetPower: 0.1,
	parallaxOffsetBase: 0,
	parallaxPowerMouse: 0,
	autoAlpha: false,
	autoRange: 0.9,
	zone: 'screens',
	mobileScaleIncrease: 0.5
};

/**
 * Soft colour-blob wash behind the centre of the screens zone.
 */
export const screensBgBlob3: HomeItemConfig = {
	disable: false,
	id: 'screens-bg-blob',
	texturex: 'systems-grid.png',
	texture: 'home-bg-colour-blob.png',
	relativeX: 0.52,
	relativeXMobile: 0.65,
	relativeY: 1,
	relativeYMobile: 0.8,
	easePower: 3,
	alpha: 0.05,
	parallaxOffsetPower: 0.1,
	parallaxOffsetBase: 0,
	parallaxPowerMouse: 0,
	autoAlpha: false,
	autoRange: 0.9,
	zone: 'screens',
	mobileScaleIncrease: 0.5
};

/**
 * Glow pass over screensBgBlob1 — same right-side anchor, second layer that
 * brightens the wash.
 */
export const screensBgBlobGlow1: HomeItemConfig = {
	disable: false,
	id: 'screens-bg-blob-glow',
	texturex: 'systems-grid.png',
	texture: 'home-bg-colour-blob.png',
	relativeX: 0.9,
	relativeY: 0.45,
	easePower: 3,
	alpha: 1e-5,
	parallaxOffsetPower: 0.03,
	parallaxOffsetBase: 0,
	parallaxPowerMouse: 0,
	autoAlpha: false,
	autoRange: 0.9,
	zone: 'screens',
	mobileScaleIncrease: 0.5
};

/**
 * Glow pass over screensBgBlob2 (left edge anchor).
 */
export const screensBgBlobGlow2: HomeItemConfig = {
	disable: false,
	id: 'screens-bg-blob-glow',
	texturex: 'systems-grid.png',
	texture: 'home-bg-colour-blob.png',
	relativeX: -0.03,
	relativeY: 0.2,
	easePower: 3,
	alpha: 0.0001,
	parallaxOffsetPower: 0.1,
	parallaxOffsetBase: 0,
	parallaxPowerMouse: 0,
	autoAlpha: false,
	autoRange: 0.9,
	zone: 'screens',
	mobileScaleIncrease: 0.5
};

/**
 * Glow pass over screensBgBlob3 (centre anchor).
 */
export const screensBgBlobGlow3: HomeItemConfig = {
	disable: false,
	id: 'screens-bg-blob-glow',
	texturex: 'systems-grid.png',
	texture: 'home-bg-colour-blob.png',
	relativeX: 0.52,
	relativeXMobile: 0.65,
	relativeY: 1,
	relativeYMobile: 0.8,
	easePower: 3,
	alpha: 0.0001,
	parallaxOffsetPower: 0.1,
	parallaxOffsetBase: 0,
	parallaxPowerMouse: 0,
	autoAlpha: false,
	autoRange: 0.9,
	zone: 'screens',
	mobileScaleIncrease: 0.5
};

/**
 * systems-grid.png reused as a faint decorative grid at the right edge of the
 * SCREENS zone (near-zero alpha, slight negative parallax). The id says
 * "systems" because of the texture, not the department.
 */
export const systemsGridScreens1: HomeItemConfig = {
	disable: false,
	id: 'systems-grid-screens1',
	texture: 'systems-grid.png',
	relativeX: 1,
	relativeY: 0.45,
	easePower: 3,
	alpha: 2e-7,
	parallaxOffsetPower: -0.01,
	parallaxOffsetBase: 0,
	parallaxPowerMouse: 0,
	autoAlpha: false,
	autoRange: 0.9,
	zone: 'screens',
	mobileScaleIncrease: 0.5
};

/**
 * Faint decorative systems-grid at the left edge of the screens zone.
 */
export const systemsGridScreens2: HomeItemConfig = {
	disable: false,
	id: 'systems-grid-screens2',
	texture: 'systems-grid.png',
	relativeX: -0.03,
	relativeY: 0.2,
	easePower: 3,
	alpha: 1.6e-6,
	parallaxOffsetPower: 0.03,
	parallaxOffsetBase: 0,
	parallaxPowerMouse: 0,
	autoAlpha: false,
	autoRange: 0.9,
	zone: 'screens',
	mobileScaleIncrease: 0.5
};

/**
 * Faint decorative systems-grid left-of-centre in the screens zone.
 */
export const systemsGridScreens3: HomeItemConfig = {
	disable: false,
	id: 'systems-grid-screens3',
	texture: 'systems-grid.png',
	relativeX: 0.07,
	relativeXMobile: 0.55,
	relativeY: 0.9,
	relativeYMobile: 0.7,
	easePower: 3,
	alpha: 2.5e-7,
	parallaxOffsetPower: -0.05,
	parallaxOffsetBase: 0,
	parallaxPowerMouse: 0,
	autoAlpha: false,
	autoRange: 0.9,
	zone: 'screens',
	mobileScaleIncrease: 0.5
};

/**
 * Large TV frame, top-left of the screens wall. maskRegion 'screens': this
 * sprite's bounds are one of the rects whose union clips the screens WebGL
 * department visual (HomeItemGeometry.getMaskRegionItems). Swaps to
 * ScreensTVMobileTop.png on mobile.
 */
export const screensTvTopLeft: HomeItemConfig = {
	disable: false,
	id: 'screensTVTopLeft',
	texture: 'screensTVTopLeft.png',
	textureMobile: 'ScreensTVMobileTop.png',
	relativeX: 0.42,
	relativeXMobile: 0.335,
	relativeY: 0.3,
	relativeYMobile: 0.19,
	easePower: 20,
	parallaxOffsetPower: -0.06,
	parallaxPowerMouse: 0,
	autoScale: false,
	zone: 'screens',
	maskRegion: 'screens'
};

/**
 * TV frame, top-right of the screens wall — mask rect for the screens WebGL
 * region. `relativeXMobilex`/`relativeYMobilex` are renamed-off (inert) mobile
 * overrides left in the data.
 */
export const screensTvTopRight: HomeItemConfig = {
	disable: false,
	id: 'screensTVTopRight',
	texture: 'screensTVTopRight.png',
	textureMobile: 'ScreensTVMobileMiddle.png',
	relativeX: 0.88,
	relativeY: 0.298,
	relativeXMobilex: 0.18,
	relativeXMobile: 0.335,
	relativeYMobilex: 0.73,
	relativeYMobile: 0.375,
	easePower: 20,
	parallaxOffsetPower: -0.03,
	parallaxPowerMouse: 0,
	autoScale: false,
	zone: 'screens',
	maskRegion: 'screens'
};

/**
 * TV frame, bottom-left of the screens wall — mask rect for the screens WebGL
 * region. Swaps to ScreensTVMobileBottom.png on mobile.
 */
export const screensTvBottomLeft: HomeItemConfig = {
	disable: false,
	id: 'screensTVBottomLeft',
	texture: 'screensTVBottomLeft.png',
	textureMobile: 'ScreensTVMobileBottomLeft.png',
	relativeX: 0.135,
	relativeXMobile: 0.23,
	relativeY: 0.585,
	relativeYMobile: 0.59,
	easePower: 20,
	parallaxOffsetPower: -0.02,
	parallaxPowerMouse: 0,
	autoScale: false,
	zone: 'screens',
	maskRegion: 'screens'
};

/**
 * TV frame, bottom-right of the screens wall — mask rect for the screens WebGL
 * region.
 */
export const screensTvBottomRight: HomeItemConfig = {
	disable: false,
	id: 'screensTVBottomRight',
	texture: 'screensTVBottomRight.png',
	textureMobile: 'ScreensTVMobileBottomRight.png',
	relativeX: 0.54,
	relativeY: 0.678,
	relativeYMobile: 0.59,
	easePower: 20,
	parallaxOffsetPower: 0.01,
	parallaxPowerMouse: 0,
	autoScale: false,
	zone: 'screens',
	maskRegion: 'screens'
};

/**
 * The cat (cat.png) sitting at the foot of the TV wall (relativeY 0.96). Pass 2 —
 * drawn above the WebGL layer. Hidden below 400px viewport width.
 */
export const screenCat: HomeItemConfig = {
	disable: false,
	id: 'screen-cat',
	texture: 'cat.png',
	alpha: 1,
	relativeX: 0.47,
	relativeXMobile: 0.58,
	relativeY: 0.96,
	easePower: 20,
	parallaxOffsetPower: 0.12,
	parallaxPowerMouse: 0.1,
	autoScale: false,
	zone: 'screens',
	pass: 2,
	hideAtWidth: 400
};

/**
 * Isometric building block (block-h-1.png). Despite the "systems-block" id these are
 * zoned to SCREENS — they decorate the screens zone and its transition toward
 * spaces. Pass 2 — drawn above the WebGL layer.
 */
export const systemsBlock1: HomeItemConfig = {
	disable: false,
	id: 'systems-block',
	texture: 'block-h-1.png',
	relativeX: 0.9,
	relativeXMobile: 0.53,
	relativeY: 0.36,
	relativeYMobile: 0.41,
	easePower: 3,
	alpha: 1e-7,
	parallaxOffsetPower: 0,
	parallaxOffsetBase: 0,
	parallaxPowerMouse: 0.0,
	autoAlpha: false,
	autoRange: 0.9,
	zone: 'screens',
	scale: 1,
	pass: 2
};

/**
 * Isometric building block (block-h-1.png). Despite the "systems-block" id these are
 * zoned to SCREENS — they decorate the screens zone and its transition toward
 * spaces. Pass 2 — drawn above the WebGL layer.
 */
export const systemsBlock2: HomeItemConfig = {
	disable: false,
	id: 'systems-block',
	texture: 'block-h-1.png',
	relativeX: 0.184,
	relativeXMobile: 0.2,
	relativeY: 0.57,
	relativeYMobile: 0.54,
	easePower: 3,
	alpha: 1e-7,
	parallaxOffsetPower: 0,
	parallaxOffsetBase: 0,
	parallaxPowerMouse: 0.0,
	autoAlpha: false,
	autoRange: 0.9,
	zone: 'screens',
	scale: 1,
	pass: 2
};

/**
 * Isometric building block (block-h-2.png) — the one block with the distinct
 * "systems-block-move" id, centred low in the screens zone. Pass 2.
 */
export const systemsBlockMove: HomeItemConfig = {
	disable: false,
	id: 'systems-block-move',
	texture: 'block-h-2.png',
	relativeX: 0.5,
	relativeY: 0.76,
	relativeYMobile: 0.56,
	easePower: 3,
	alpha: 1e-7,
	parallaxOffsetPower: 0,
	parallaxOffsetBase: 0,
	parallaxPowerMouse: 0.0,
	autoAlpha: false,
	autoRange: 0.9,
	zone: 'screens',
	scale: 1,
	pass: 2
};

/**
 * Isometric building block (block-v-2.png). Despite the "systems-block" id these are
 * zoned to SCREENS — they decorate the screens zone and its transition toward
 * spaces. Pass 2 — drawn above the WebGL layer.
 */
export const systemsBlock3: HomeItemConfig = {
	disable: false,
	id: 'systems-block',
	texture: 'block-v-2.png',
	relativeX: 0.985,
	relativeXMobile: 0.625,
	relativeY: 0.3,
	easePower: 3,
	alpha: 1e-7,
	parallaxOffsetPower: 0,
	parallaxOffsetBase: 0,
	parallaxPowerMouse: 0.0,
	autoAlpha: false,
	autoRange: 0.9,
	zone: 'screens',
	scale: 1,
	pass: 2
};

/**
 * Isometric building block (block-v-1.png). Despite the "systems-block" id these are
 * zoned to SCREENS — they decorate the screens zone and its transition toward
 * spaces. Pass 2 — drawn above the WebGL layer.
 */
export const systemsBlock4: HomeItemConfig = {
	disable: false,
	id: 'systems-block',
	texture: 'block-v-1.png',
	relativeX: 0,
	relativeXMobile: 0.05,
	relativeY: 0.535,
	relativeYMobile: 0.5,
	easePower: 3,
	alpha: 1e-7,
	parallaxOffsetPower: 0,
	parallaxOffsetBase: 0,
	parallaxPowerMouse: 0.0,
	autoAlpha: false,
	autoRange: 0.9,
	zone: 'screens',
	scale: 1,
	pass: 2
};

/**
 * Isometric building block (block-v-1.png). Despite the "systems-block" id these are
 * zoned to SCREENS — they decorate the screens zone and its transition toward
 * spaces. Pass 2 — drawn above the WebGL layer.
 */
export const systemsBlock5: HomeItemConfig = {
	disable: false,
	id: 'systems-block',
	texture: 'block-v-1.png',
	relativeX: 0,
	relativeXMobile: 0.05,
	relativeY: 0.59,
	easePower: 3,
	alpha: 1e-7,
	parallaxOffsetPower: 0,
	parallaxOffsetBase: 0,
	parallaxPowerMouse: 0.0,
	autoAlpha: false,
	autoRange: 0.9,
	zone: 'screens',
	scale: 1,
	pass: 2
};

/**
 * Isometric building block (block-v-2.png). Despite the "systems-block" id these are
 * zoned to SCREENS — they decorate the screens zone and its transition toward
 * spaces. Pass 2 — drawn above the WebGL layer.
 */
export const systemsBlock6: HomeItemConfig = {
	disable: false,
	id: 'systems-block',
	texture: 'block-v-2.png',
	relativeX: 0.18,
	relativeXMobile: 0.05,
	relativeY: 0.36,
	relativeYMobile: 0.2,
	easePower: 3,
	alpha: 1e-7,
	parallaxOffsetPower: 0,
	parallaxOffsetBase: 0,
	parallaxPowerMouse: 0.0,
	autoAlpha: false,
	autoRange: 0.9,
	zone: 'screens',
	scale: 1,
	pass: 2
};

/**
 * Isometric building block (block-h-2.png). Despite the "systems-block" id these are
 * zoned to SCREENS — they decorate the screens zone and its transition toward
 * spaces. Pass 2 — drawn above the WebGL layer.
 */
export const systemsBlock7: HomeItemConfig = {
	disable: false,
	id: 'systems-block',
	texture: 'block-h-2.png',
	relativeX: 0.16,
	relativeXMobile: 0.19,
	relativeY: 0.655,
	relativeYMobile: 0.605,
	easePower: 3,
	alpha: 1e-7,
	parallaxOffsetPower: 0,
	parallaxOffsetBase: 0,
	parallaxPowerMouse: 0.0,
	autoAlpha: false,
	autoRange: 0.9,
	zone: 'screens',
	scale: 1,
	pass: 2
};

/**
 * Isometric building block (block-h-1.png). Despite the "systems-block" id these are
 * zoned to SCREENS — they decorate the screens zone and its transition toward
 * spaces. Pass 2 — drawn above the WebGL layer.
 */
export const systemsBlock8: HomeItemConfig = {
	disable: false,
	id: 'systems-block',
	texture: 'block-h-1.png',
	relativeX: 0.55,
	relativeXMobile: 0.4,
	relativeY: 0.25,
	easePower: 3,
	alpha: 1e-7,
	parallaxOffsetPower: 0,
	parallaxOffsetBase: 0,
	parallaxPowerMouse: 0.0,
	autoAlpha: false,
	autoRange: 0.9,
	zone: 'screens',
	scale: 1,
	pass: 2
};

/**
 * Isometric building block (block-h-1.png). Despite the "systems-block" id these are
 * zoned to SCREENS — they decorate the screens zone and its transition toward
 * spaces. Pass 2 — drawn above the WebGL layer.
 */
export const systemsBlock9: HomeItemConfig = {
	disable: false,
	id: 'systems-block',
	texture: 'block-h-1.png',
	relativeX: 0.47,
	relativeXMobile: 0.32,
	relativeY: 0.3,
	easePower: 3,
	alpha: 1e-7,
	parallaxOffsetPower: 0,
	parallaxOffsetBase: 0,
	parallaxPowerMouse: 0.0,
	autoAlpha: false,
	autoRange: 0.9,
	zone: 'screens',
	scale: 1,
	pass: 2
};

/**
 * Isometric building block (block-h-1.png). Despite the "systems-block" id these are
 * zoned to SCREENS — they decorate the screens zone and its transition toward
 * spaces. Pass 2 — drawn above the WebGL layer.
 */
export const systemsBlock10: HomeItemConfig = {
	disable: false,
	id: 'systems-block',
	texture: 'block-h-1.png',
	relativeX: 0.38,
	relativeXMobile: 0.23,
	relativeY: 0.35,
	easePower: 3,
	alpha: 1e-7,
	parallaxOffsetPower: 0,
	parallaxOffsetBase: 0,
	parallaxPowerMouse: 0.0,
	autoAlpha: false,
	autoRange: 0.9,
	zone: 'screens',
	scale: 1,
	pass: 2
};
